import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "../../assets/images/disktop-logo.png";
import StatusBadge from "../vendors/StatusBadge";
import { adminNavigation, vendorNavigation } from "../../utils/constants";
import { useAuthStore } from "../../store/useAuthStore";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const NavItem = ({ item, isActive, isDisabled }) => {
  const Wrapper = isDisabled ? "div" : Link;

  return (
    <Wrapper
      {...(!isDisabled && { to: item.href })}
      aria-disabled={isDisabled}
      className={classNames(
        "flex items-center gap-x-3 rounded-xl px-3 py-2.5 text-sm transition-colors",
        isDisabled
          ? "opacity-50 cursor-not-allowed pointer-events-none"
          : isActive
          ? "bg-toucanTeal shadow-sm"
          : "hover:bg-[#DEFAFF]"
      )}
    >
      <div
        className={classNames(
          isActive ? "bg-[#1FADC6]" : "bg-[#B8E6F5]",
          "flex h-10 w-10 items-center justify-center rounded-full"
        )}
      >
        <item.icon
          className={classNames(
            isActive ? "text-white" : "text-[#1FADC6]",
            "h-5 w-5"
          )}
        />
      </div>

      <span
        className={classNames(
          isActive ? "text-gray-900 font-semibold" : "text-gray-700",
          "font-medium"
        )}
      >
        {item.name}
      </span>
    </Wrapper>
  );
};

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // 🔹 ROLE DETECTION
  const { role } = useAuthStore();

  const vendorStatus = useAuthStore.getState().vendorStatus;

  const navigation =
    role?.toLowerCase() === "admin" ? adminNavigation : vendorNavigation;

  return (
    <>
      {/* Mobile Toggle */}
      <button
        onClick={() => setIsOpen(true)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg"
      >
        <svg
          className="h-6 w-6 text-gray-700"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
      </button>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={classNames(
          "fixed md:static left-0 z-50 w-[260px]",
          "inset-y-0 h-screen", // 🔥 IMPORTANT
          "bg-gradient-to-b from-[#EAF8FF] to-[#F7FBFF]",
          "border-r border-gray-200",
          "transform transition-transform duration-300",
          isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}
      >
        <div className="flex flex-col min-h-screen px-4 py-6 overflow-y-auto">
          {/* Logo */}
          {isOpen && (
            <div className="mb-6 px-2 md:hidden">
              <img src={Logo} alt="Logo" className="h-8" />
            </div>
          )}

          {/* Navigation */}
          <nav className="flex flex-col space-y-2 flex-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;

              const isDisabled =
                role === "Vendor" &&
                vendorStatus === "Pending" &&
                item.name === "Add New Experience";

              return (
                <NavItem
                  key={item.name}
                  item={item}
                  isActive={isActive}
                  isDisabled={isDisabled}
                />
              );
            })}
          </nav>

          {/* Status */}
          {role === "Vendor" && (
            <div className="mt-auto pt-6 border-t border-gray-200/50">
              <StatusBadge status={vendorStatus} sidebar={true} />
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
